import { OhlqTimelineEntry } from '../../core/models';

// Turns a stored OHLQ event into a sentence an operator can read.
//
// The keys are the STORED ALIASES, not class names — that is the whole point of the middleware aliasing
// its events: a record can be renamed on the server without this file going stale, and an alias that
// reaches here unrecognised still renders as itself rather than vanishing.

// The state carrier. It accompanies every changeset and says only "here is where the item landed", which
// the change rows beside it already describe field by field — so showing it would double every entry with
// a line carrying no information. The FIRST carrier is different: it is the opening fact of the stream and
// is described below.
const STATE_CARRIER = 'ohlq_item_state_recorded_v1';

/** The timeline as it should be shown: the facts, without the bookkeeping. */
export function visibleOhlqTimeline(entries: OhlqTimelineEntry[]): OhlqTimelineEntry[] {
    return (entries ?? []).filter((e) => e.type !== STATE_CARRIER);
}

export function describeOhlqEvent(entry: OhlqTimelineEntry): string {
    const d = (entry.data ?? {}) as Record<string, unknown>;

    switch (entry.type) {
        case 'ohlq_item_first_seen_v1':
            return 'First seen in the Ohio catalog';

        case 'ohlq_price_changed_v1': {
            const from = money(d['oldPrice']);
            const to = money(d['newPrice']);
            const on = date(d['effectiveDate']);
            return `Price ${from} → ${to}${on ? `, effective ${on}` : ''}`;
        }

        case 'ohlq_future_price_scheduled_v1': {
            const to = money(d['price']);
            const on = date(d['effectiveDate']);
            const now = money(d['currentPrice']);
            return `Price change scheduled: ${now} → ${to}${on ? ` from ${on}` : ''}`;
        }

        case 'ohlq_listing_visibility_changed_v1':
            return d['newVisible'] === true
                ? 'Listed as sellable on the website'
                : 'No longer listed as sellable on the website';

        case 'ohlq_purchase_limit_changed_v1': {
            const to = Number(d['newLimit'] ?? 0);
            return to > 0
                ? `Purchase limit set to ${to} per order`
                : 'Purchase limit lifted';
        }

        case 'ohlq_sales_status_changed_v1':
            return `State status ${text(d['oldCode']) || '—'} → ${text(d['newCode']) || '—'}`;

        case 'ohlq_bottling_changed_v1': {
            const from = text(d['oldUpc']);
            const to = text(d['newUpc']);
            const count = Number(d['upcCount'] ?? 0);
            const suffix = count > 1 ? ` (${count} barcodes on file)` : '';
            return from ? `Barcode ${from} → ${to}${suffix}` : `Barcode set to ${to}${suffix}`;
        }

        case 'ohlq_family_key_changed_v1': {
            const to = text(d['newFamilyKey']);
            const from = text(d['oldFamilyKey']);
            return from
                ? `Moved to the “${to}” product family (was “${from}”)`
                : `Grouped into the “${to}” product family`;
        }

        case 'ohlq_feed_coverage_changed_v1':
            return `Known from ${coverage(d['oldCoverage'])} → ${coverage(d['newCoverage'])}`;

        case 'ohlq_item_field_changed_v1': {
            const field = label(text(d['field']));
            const from = text(d['oldValue']);
            const to = text(d['newValue']);
            if (!from) return `${field} set to “${to}”`;
            if (!to) return `${field} cleared (was “${from}”)`;
            return `${field}: “${from}” → “${to}”`;
        }

        case 'ohlq_item_missing_from_feed_v1':
            return `Absent from the feed — ${text(d['reason']) || 'no reason recorded'}`;

        case STATE_CARRIER:
            return 'Snapshot recorded';

        default:
            // An event this build has not met. Show its stored name rather than nothing: an unfamiliar
            // line an operator can quote is worth more than a silently dropped one.
            return entry.type;
    }
}

const FIELD_LABELS: Record<string, string> = {
    Name: 'Name',
    Category: 'Category',
    Type: 'Type',
    SubType: 'Sub-type',
    ShortDescription: 'Description',
    Volume: 'Size',
    ImageThumb: 'Thumbnail',
    ImageFront: 'Image',
    Ounces: 'Ounces',
    Proof: 'Proof',
    CurrentEffectiveDate: 'Price effective date'
};

function label(field: string): string {
    return FIELD_LABELS[field] ?? field;
}

function coverage(value: unknown): string {
    switch (text(value)) {
        case 'Both': return 'both feeds';
        case 'PriceOnly': return 'the price feed only';
        case 'ProductOnly': return 'the product feed only';
        default: return text(value) || '—';
    }
}

function money(value: unknown): string {
    const n = Number(value);
    return Number.isFinite(n) ? n.toFixed(2) : '—';
}

function date(value: unknown): string {
    const raw = text(value);
    if (!raw) return '';
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? raw : parsed.toLocaleDateString();
}

function text(value: unknown): string {
    return value == null ? '' : String(value);
}
